import { Document } from "@tiptap/extension-document";
import { Text } from "@tiptap/extension-text";
import { Paragraph } from "@tiptap/extension-paragraph";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import ListItem from "@tiptap/extension-list-item";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import { StarterKit } from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import ResizableImageExtension from "./ResizableImageExtension";
import { Attachment, UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import { JSONContent } from "@tiptap/react";
import Recommendations from "./extensions/Recommendations";
import ProductLink from "./extensions/ProductLink";
import CanvaLink from "./extensions/CanvaLink";
import Loom from "./extensions/Loom";
import { Youtube } from "@tiptap/extension-youtube";
import UrlShortener from "./extensions/UrlShortener";
import CustomLink from "./extensions/CustomLink";
import { Underline } from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { Editor } from "@tiptap/core";
import { getTableExtensions } from "./extensions/tableUtils";
import URLLink from "./extensions/URLLink";
import History from "@tiptap/extension-history";
import { downsizeImages } from "common/imageUtils";
import { convertHEICImages } from "common/imageUtils";

// Url shortener matching.  Ignores sensitive words part of the query string
// Requires atleast 18 characters (the length of the shortened url)
export const SHORT_URL_REGEX =
  /^https?:\/\/(?!.*?[?&](login|token|password|auth|oauth|signin|signup|reset|confirm|activate|admin|api|secret|key|session|cookie)=)[^\s]{18,}$/;
export const PLACEHOLDER_PATH = "/assets/images/image-placeholder.png";

type JSONValue = { [key: string]: any } | JSONValue[];
const renderInitialContent = (
  content: string | JSONContent | undefined,
  attachments: Attachment[],
) => {
  // Handle first load of attachments by swapping in the URLs.
  const attachmentMap = attachments.reduce(
    (mapping, a) => {
      mapping[a.inlineReference] = a;
      return mapping;
    },
    {} as { [inlineReference: string]: Attachment },
  );

  const _step = (node: JSONValue): JSONValue => {
    if (Array.isArray(node)) {
      return node.map(_step);
    } else if (typeof node === "object" && node !== null) {
      if (node.type === "image") {
        node.attrs = node.attrs || {};
        if (!node.attrs.src) {
          if (attachmentMap[node.attrs.title]) {
            node.attrs.src = attachmentMap[node.attrs.title].url;
          } else {
            node.attrs.src = PLACEHOLDER_PATH;
          }
        }
      }
      if (node.content) {
        node.content = node.content.map(_step);
      }
    }
    return node;
  };
  // Null is typeof object === "object"
  if (content !== null && typeof content === "object") {
    // If the content is an empty object, return undefined.
    if (Object.keys(content).length === 0 && content.constructor === Object) {
      return undefined;
    }
    return _step(content);
  }
  return content;
};

const extractImageReferences = (content: JSONContent) => {
  const references = new Set<string>([]);

  const traverseNodes = (node: JSONValue) => {
    if (Array.isArray(node)) {
      node.map(traverseNodes);
    } else if (typeof node === "object" && node !== null) {
      if (node.type === "image") {
        references.add(node.attrs.title);
      }
      if (node.content) {
        node.content.map(traverseNodes);
      }
    }
    return node;
  };
  traverseNodes(content);
  return references;
};

const generateRandomName = (prefix?: string) => {
  return Math.random()
    .toString(36)
    .replace("0.", prefix || "");
};

const getExtensions = ({
  placeholder,
  editable = true,
  urlShortenerEnabled = false,
  isRichTextEnabled = true,
  handleCommunications = false,
  linkUnfurlEnabled = false,
  imageOptions,
}: {
  placeholder?: string;
  editable?: boolean;
  urlShortenerEnabled?: boolean;
  isRichTextEnabled?: boolean;
  handleCommunications?: boolean;
  linkUnfurlEnabled?: boolean;
  imageOptions?: {
    showDownload?: boolean;
    showZoom?: boolean;
    showModal?: boolean;
  };
}) => {
  return [
    // https://tiptap.dev/api/nodes/task-list
    ...(urlShortenerEnabled ? [UrlShortener] : []),
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    // TextStyle.configure({ types: [ListItem.name] }),
    ...(isRichTextEnabled
      ? [
          StarterKit.configure({
            bulletList: {
              keepMarks: true,
              keepAttributes: false, // TODO : Making this as `false` because marks are not preserved when I try to preserve attrs, awaiting a bit of help
              HTMLAttributes: {
                style: "margin: 4px 0 !important; padding-inline-start: 32px;",
              },
            },
            orderedList: {
              keepMarks: true,
              keepAttributes: false, // TODO : Making this as `false` because marks are not preserved when I try to preserve attrs, awaiting a bit of help
              HTMLAttributes: {
                style: "margin: 4px 0 !important; padding-inline-start: 32px;",
              },
            },
            heading: {
              levels: [1, 2, 3, 4, 5],
              HTMLAttributes: {
                style: "margin: 16px 0 4px 0 !important",
              },
            },
          }),
          Underline,
          TextStyle,
          Highlight.configure({ multicolor: true }),
          CustomLink.configure({
            openOnClick: true,
            emailUrl: handleCommunications
              ? (email) =>
                  `/inbox/email/create?email=${encodeURIComponent(email)}`
              : null,
          }),
          TaskList,
          TaskItem.configure({
            nested: true,
          }),
          // This is an improvement on the vanilla "Image" extension that allows us to resize images.,
          ResizableImageExtension.configure({
            inline: true,
            ...imageOptions,
          }),
          // Add to group that is "configured"
          Recommendations,
          ProductLink,
          ...getTableExtensions(editable),
        ]
      : [Document, Text, Paragraph, HardBreak, History]),

    ...(placeholder === undefined
      ? []
      : [
          Placeholder.configure({
            placeholder: placeholder,
            showOnlyWhenEditable: false,
          }),
        ]),
    ...(linkUnfurlEnabled
      ? [
          URLLink.configure({
            addPasteHandler: true,
          }),
        ]
      : []),
    Youtube.configure({
      addPasteHandler: true,
      nocookie: true,
    }),
    Loom.configure({
      addPasteHandler: true,
      allowFullscreen: true,
      hideEmbedTopBar: false,
      hideOwner: true,
    }),
    CanvaLink.configure({
      addPasteHandler: true,
    }),
  ];
};
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export type RichContent = {
  text: string;
  html: string;
  json: string;
  attachments: PlainMessage<UploadAttachment>[];
};

type JSONNode = Record<string, any> | JSONNode[];

const stripImageSrc = (input: string) => {
  const parsed = JSON.parse(input);
  // Recursive function to traverse and modify the JSON structure
  const _step = (node: JSONNode): JSONNode => {
    if (Array.isArray(node)) {
      return node.map((item) => _step(item));
    }

    if (typeof node === "object" && node !== null) {
      if (node["type"] === "image") {
        node.attrs = { ...(node.attrs ?? {}), src: "" };
      }

      if (node["content"]) {
        node["content"] = node["content"].map(_step);
      }
    }

    return node;
  };

  // Modify the data and return it as a string
  return JSON.stringify(_step(parsed));
};

const addImagePlaceholder = async (
  editor: Editor | null,
  referenceName: string,
  file: File,
  replaceSelection: boolean,
  resized: string = "false",
) => {
  const base64 = await blobToBase64(file);
  const src = base64 || PLACEHOLDER_PATH;
  if (editor) {
    if (replaceSelection) {
      editor
        .chain()
        .focus()
        .setImage({ src: src, title: referenceName })
        .setNodeSelection(editor.state.selection.from)
        .updateAttributes("image", {
          resized: resized,
        })
        .run();
    } else {
      // Use insertContent instead of setImage so we don't overwrite any content
      const { to } = editor.state.selection;

      // Move the cursor to the end of the selection range
      editor.chain().focus().setTextSelection(to).run();

      // Insert the image at the new cursor position
      editor
        .chain()
        .focus()
        .insertContent({
          type: "image",
          attrs: {
            src: src,
            title: referenceName,
          },
        })
        .run();
    }
  }
};

const processImages = async (
  files: File[],
  editor: Editor | null,
  onUpload: (
    files: FileList | File[] | null,
    inlineReferences?: (string | null)[],
    callback?: (u: PlainMessage<UploadAttachment>[]) => void,
  ) => void,
  setAttachments: (attachments: PlainMessage<UploadAttachment>[]) => void,
  inlineAttachmentsEnabled: boolean,
  externalAttachmentHandler?: (files: File[]) => void,
) => {
  const convertedFiles = await convertHEICImages(files);
  const resizedFiles = await downsizeImages(convertedFiles, null, null, 800);

  if (externalAttachmentHandler) {
    externalAttachmentHandler(resizedFiles.map((res) => res.file));
    return;
  }

  if (!inlineAttachmentsEnabled) {
    onUpload(
      resizedFiles.map((res) => res.file),
      undefined,
      setAttachments,
    );
    return;
  }

  const references: (string | null)[] = [];
  for (const result of resizedFiles) {
    if (result.file.type.startsWith("image/")) {
      const name = generateRandomName("pasted-");
      addImagePlaceholder(
        editor,
        name,
        result.file,
        true,
        result.resized.toString(),
      );
      references.push(name);
    } else {
      references.push(null);
    }
    onUpload(
      resizedFiles.map((res) => res.file),
      references,
      setAttachments,
    );
  }
};

export {
  getExtensions,
  generateRandomName,
  renderInitialContent,
  extractImageReferences,
  blobToBase64,
  stripImageSrc,
  addImagePlaceholder,
  processImages,
};
